import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormProvider,
  useForm,
  useWatch,
  type FieldPath,
  type Resolver,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, isValid, parse } from 'date-fns';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CharacterCount,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormDialogFooter,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectPlaceholder,
  TextField,
  useQuickCreateMenuPaper,
  TimePicker,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  attemptFormDialogClose,
  useConfirm,
} from '@/shared/ui';
import { AuthAvatar } from '@/shared/avatar';
import { staffPickerInitials } from '@/shared/ui/form/StaffAssigneePicker';
import { FormPage } from '@/shared/pages';
import { toast } from '@/shared/feedback/toast';
import { formatPatientOptionLabel } from '@/shared/utils/validation';
import { ClinicScopeSelect, clinicDisplayName, useClinicScopeField } from '@/shared/clinic';
import { usePermission } from '@/shared/hooks/usePermission';
import { Patient } from '@/shared/types/patient';
import type { Lead } from '@/modules/lead/api/lead.api';
import type { BookingDoctorOption } from '@/modules/scheduling/api/availability.api';
import { displayPatientName } from '@/modules/patient/utils/patientForm';
import PatientSelect from '@/modules/patient/components/PatientSelect';
import TreatmentServiceAutocomplete from '@/modules/treatment-catalog/components/TreatmentServiceAutocomplete';
import { formatPatientInputLabel } from '@/modules/patient/utils/patientOptionLabel';
import {
  APPOINTMENT_STATUS_OPTIONS,
  getFormStatusOptions,
  getStatusColors,
  getStatusLabel,
  isTerminalStatus,
} from '../utils/appointmentStatus';
import {
  CANCEL_REASON_MAX_LENGTH,
  buildAppointmentValidationSchema,
  buildEditValidationSchema,
  calculateEndTimeLabel,
  validateRequiredChair,
  type AppointmentFormMode,
  type AppointmentFormValues,
  type AppointmentSubjectType,
  type AppointmentVisitKind,
} from '../utils/appointmentForm';
import { useAppointmentPatientSearch } from '../hooks/useAppointmentPatientSearch';
import { useAppointmentLeadSearch } from '../hooks/useAppointmentLeadSearch';
import { useAppointmentFormBooking } from '../hooks/useAppointmentFormBooking';
import { useAppointmentFormLifecycle } from '../hooks/useAppointmentFormLifecycle';
import { useSuggestedVisitKind } from '../hooks/useSuggestedVisitKind';
import AppointmentDurationField from './AppointmentDurationField';
import AppointmentFormSkeleton from './AppointmentFormSkeleton';
import AppointmentSchedulePanel from './AppointmentSchedulePanel';
import AppointmentTextFields, {
  type AppointmentTextFieldValues,
} from './AppointmentTextFields';
import AppointmentTypeSelect from './AppointmentTypeSelect';
import ChairSelect from './ChairSelect';
import QuickCreatePatientDialog from './QuickCreatePatientDialog';
import QuickCreateLeadDialog from './QuickCreateLeadDialog';
import TreatmentCatalogItemFormDialog from '@/modules/treatment-catalog/components/TreatmentCatalogItemFormDialog';
import type { TreatmentCatalogItem } from '@/modules/treatment-catalog/types/treatmentCatalog.types';
import { applyAppointmentCascadeClear } from '../utils/appointmentCascade';
import type { AppointmentCascadeField } from '../utils/appointmentCascade';

export type AppointmentFormProps = {
  mode: AppointmentFormMode;
  title: string;
  /** Page = FormPage shell; dialog = centered modal (patient chart / inbox overlay). */
  presentation?: 'page' | 'dialog';
  /** Lock subject to patient (nested create from Patient Details). */
  lockPatient?: boolean;
  /** Lock subject to lead (create from inbox when only a Lead is linked). */
  lockLead?: boolean;
  backTo?: string;
  onBack?: () => void;
  submitLabel: string;
  submittingLabel: string;
  submitting?: boolean;
  error?: string | null;
  initialValues: AppointmentFormValues;
  /** Create: prefill after seed patient loads (query or location state). */
  prefillPatientId?: string | null;
  /** Create: prefill lead (book from Lead Details before convert). */
  prefillLeadId?: string | null;
  prefillClinicId?: string | null;
  prefillDoctorId?: string | null;
  prefillDate?: string | null;
  prefillStartTime?: string | null;
  /** From calendar preflight when opening a warn slot. */
  showPrefillWarnBanner?: boolean;
  includeInactiveTypeIds?: string[];
  includeInactiveChairIds?: string[];
  excludeAppointmentId?: string;
  onSubmit: (values: AppointmentFormValues) => void;
  /** Dialog presentation only. Stays mounted until the exit transition finishes. */
  dialogOpen?: boolean;
  onDialogExited?: () => void;
};

type FormShellProps = {
  presentation: 'page' | 'dialog';
  title: string;
  onBack: () => void;
  onSubmit: () => void;
  submitLabel: string;
  busySubmitLabel: string;
  submitting?: boolean;
  /** Title X and Esc leave without the Hủy confirm. Backdrop still does not close. */
  onDismiss?: () => void;
  /** Dialog presentation. Page shell ignores this. */
  open?: boolean;
  onExited?: () => void;
  error?: string | null;
  children: React.ReactNode;
};

const doctorOptionLabel = (doctor: BookingDoctorOption): string => {
  const name = `${doctor.lastName} ${doctor.firstName}`.trim();
  return doctor.specialization ? `BS. ${name} (${doctor.specialization})` : `BS. ${name}`;
};

const DoctorOptionContent: React.FC<{ doctor: BookingDoctorOption }> = ({ doctor }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <AuthAvatar apiSrc={doctor.avatarUrl} sx={{ width: 20, height: 20, fontSize: 10, flexShrink: 0 }}>
      {staffPickerInitials(doctor)}
    </AuthAvatar>
    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {doctorOptionLabel(doctor)}
    </Box>
  </Box>
);

const StatusSwatch: React.FC<{ status: string }> = ({ status }) => {
  const colors = getStatusColors(status);
  return (
    <Box
      component="span"
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: colors.border,
        flexShrink: 0,
      }}
    />
  );
};

const StatusOptionContent: React.FC<{ status: string }> = ({ status }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <StatusSwatch status={status} />
    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {getStatusLabel(status)}
    </Box>
  </Box>
);

const AppointmentFormShell: React.FC<FormShellProps> = ({
  presentation,
  title,
  onBack,
  onSubmit,
  submitLabel,
  busySubmitLabel,
  submitting = false,
  onDismiss,
  open = true,
  onExited,
  error = null,
  children,
}) => {
  const { t } = useTranslation('common');

  if (presentation === 'dialog') {
    return (
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        TransitionProps={{ onExited }}
        onClose={(_event, reason) => {
          if (submitting) return;
          if (onDismiss) {
            if (reason === 'backdropClick') return;
            onDismiss();
            return;
          }
          onBack();
        }}
        aria-labelledby="appointment-form-dialog-title"
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              height: 'auto',
              minHeight: 'min(640px, calc(100% - 128px))',
              maxHeight: 'calc(100% - 128px)',
            },
          },
        }}
      >
        <DialogTitle
          id="appointment-form-dialog-title"
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            ...(onDismiss ? { position: 'relative', pr: 6 } : null),
          }}
        >
          {title}
          {onDismiss ? (
            <IconButton
              aria-label={t('close')}
              onClick={onDismiss}
              disabled={submitting}
              size="small"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
          {error ? (
            <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
              {error}
            </Alert>
          ) : null}
          {children}
        </DialogContent>
        <FormDialogFooter
          busy={submitting}
          onCancel={onBack}
          onSubmit={onSubmit}
          submitLabel={submitLabel}
          busySubmitLabel={busySubmitLabel}
          cancelVariant="text"
        />
      </Dialog>
    );
  }

  return (
    <FormPage
      title={title}
      onBack={onBack}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
      submitting={submitting}
      error={error}
      maxWidth="lg"
    >
      {children}
    </FormPage>
  );
};

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  mode,
  title,
  presentation = 'page',
  lockPatient = false,
  lockLead = false,
  backTo,
  onBack,
  submitLabel,
  submittingLabel,
  submitting = false,
  error = null,
  initialValues,
  prefillPatientId = null,
  prefillLeadId = null,
  prefillClinicId = null,
  prefillDoctorId = null,
  prefillDate = null,
  prefillStartTime = null,
  showPrefillWarnBanner = false,
  includeInactiveTypeIds = [],
  includeInactiveChairIds = [],
  excludeAppointmentId,
  onSubmit,
  dialogOpen: appointmentDialogOpen = true,
  onDialogExited,
}) => {
  const { t } = useTranslation(['appointment', 'common']);
  const { t: tTreatment } = useTranslation('treatment');
  const confirm = useConfirm();
  const { hasPermission } = usePermission();
  const canCreatePatient = hasPermission('patients:create');
  const canCreateLead = hasPermission('leads:create');
  const canCreateTreatment = hasPermission('treatment-catalog:create');
  const textFieldsRef = useRef<AppointmentTextFieldValues>({
    reasonForVisit: initialValues.reasonForVisit,
    notes: initialValues.notes,
  });
  const patientInputRef = useRef('');
  const leadInputRef = useRef('');
  const [createTreatmentOpen, setCreateTreatmentOpen] = useState(false);
  const [pinnedTreatment, setPinnedTreatment] = useState<TreatmentCatalogItem | null>(null);
  const [quickCreatePatientOpen, setQuickCreatePatientOpen] = useState(false);
  const [quickCreateLeadOpen, setQuickCreateLeadOpen] = useState(false);
  const [quickCreateFirstName, setQuickCreateFirstName] = useState('');

  const {
    clinics,
    clinicId: scopeClinicId,
    setClinicId: setScopeClinicId,
    showClinicPicker,
    scopedClinicName,
  } = useClinicScopeField({
    open: true,
    initialClinicId:
      mode === 'edit'
        ? initialValues.clinicId
        : prefillClinicId || undefined,
  });

  const validationSchema = useMemo(
    () =>
      mode === 'edit'
        ? buildEditValidationSchema(initialValues.status, t)
        : buildAppointmentValidationSchema('create', t),
    [mode, initialValues.status, t]
  );

  // Resolve clinic/doctor before first paint so chairs + calendar bootstrap with the skeleton.
  const knownClinicId =
    mode === 'edit'
      ? initialValues.clinicId
      : prefillClinicId || scopeClinicId || '';
  const knownDoctorId =
    mode === 'edit' ? initialValues.doctorId : prefillDoctorId || '';
  const parsedPrefillDate =
    prefillDate && !Number.isNaN(new Date(`${prefillDate}T12:00:00`).getTime())
      ? new Date(`${prefillDate}T12:00:00`)
      : null;

  const mergedInitialValues = useMemo(
    (): AppointmentFormValues => ({
      ...initialValues,
      clinicId: initialValues.clinicId || knownClinicId,
      doctorId: initialValues.doctorId || knownDoctorId,
      date: initialValues.date || parsedPrefillDate,
      startTime: initialValues.startTime || prefillStartTime || '',
    }),
    [
      initialValues,
      knownClinicId,
      knownDoctorId,
      parsedPrefillDate,
      prefillStartTime,
    ]
  );

  const form = useForm<AppointmentFormValues>({
    defaultValues: mergedInitialValues,
    resolver: yupResolver(validationSchema, {
      stripUnknown: false,
    }) as unknown as Resolver<AppointmentFormValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = form;

  useEffect(() => {
    if (mode === 'edit') {
      form.reset(mergedInitialValues);
    }
  }, [form, mode, mergedInitialValues]);

  const [
    subjectType,
    patientId,
    leadId,
    doctorId,
    clinicId,
    chairId,
    date,
    startTime,
    duration,
    type,
    visitKind,
    status,
    reasonForVisit,
    notes,
    treatmentId,
    cancelReason,
  ] = useWatch({
    control,
    name: [
      'subjectType',
      'patientId',
      'leadId',
      'doctorId',
      'clinicId',
      'chairId',
      'date',
      'startTime',
      'duration',
      'type',
      'visitKind',
      'status',
      'reasonForVisit',
      'notes',
      'treatmentId',
      'cancelReason',
    ],
  });

  const values = useMemo(
    (): AppointmentFormValues => ({
      subjectType: subjectType ?? 'patient',
      patientId: patientId ?? '',
      leadId: leadId ?? '',
      doctorId: doctorId ?? '',
      clinicId: clinicId ?? '',
      chairId: chairId ?? '',
      date: date ?? null,
      startTime: startTime ?? '',
      duration: duration ?? null,
      type: type ?? '',
      visitKind: visitKind ?? '',
      status: status ?? 'scheduled',
      reasonForVisit: reasonForVisit ?? '',
      notes: notes ?? '',
      treatmentId: treatmentId ?? '',
      cancelReason: cancelReason ?? '',
    }),
    [
      subjectType,
      patientId,
      leadId,
      doctorId,
      clinicId,
      chairId,
      date,
      startTime,
      duration,
      type,
      visitKind,
      status,
      reasonForVisit,
      notes,
      treatmentId,
      cancelReason,
    ]
  );

  const fieldHelper = useCallback(
    (name: FieldPath<AppointmentFormValues>) => {
      if (!isSubmitted) return { error: false, helperText: undefined as string | undefined };
      const message = errors[name]?.message;
      return {
        error: Boolean(message),
        helperText: message ? String(message) : undefined,
      };
    },
    [errors, isSubmitted]
  );

  const booking = useAppointmentFormBooking({
    mode,
    initialValues,
    prefillClinicId,
    scopeClinicId,
    includeInactiveTypeIds,
    includeInactiveChairIds,
    excludeAppointmentId,
    values,
  });

  const {
    catalogQuery,
    catalogItems,
    doctors,
    bookingAnchor,
    bookingPolicy,
    bookingChairs,
    selectedDoctor,
    requireChairModeRef,
    chairRequired,
    activeChairCountRef,
    handleActiveChairCountChange,
    resetActiveChairCount,
    isBootstrapPending,
    isDoctorsPending,
    isDoctorsListUnready,
  } = booking;

  const treatmentOptions = useMemo(() => {
    if (
      pinnedTreatment &&
      !catalogItems.some((item) => item._id === pinnedTreatment._id)
    ) {
      return [pinnedTreatment, ...catalogItems];
    }
    return catalogItems;
  }, [catalogItems, pinnedTreatment]);

  const selectedTreatment =
    treatmentOptions.find((item) => item._id === values.treatmentId) ?? null;

  const patientSeedId =
    mode === 'edit' ? initialValues.patientId : prefillPatientId;
  const leadSeedId = mode === 'edit' ? initialValues.leadId : prefillLeadId;

  const patientSearch = useAppointmentPatientSearch({
    selectedPatientId: values.patientId,
    seedPatientId: patientSeedId,
  });
  const {
    selectedPatient,
    seedPending: patientSeedPending,
    seedError: patientSeedError,
    rememberSelected: rememberSelectedPatient,
  } = patientSearch;

  const {
    options: leadOptions,
    selectedLead,
    searching: leadSearching,
    queryTooShort: leadQueryTooShort,
    seedPending: leadSeedPending,
    seedError: leadSeedError,
    handleInputChange: handleLeadInputChange,
    handleOpen: handleLeadOpen,
    rememberSelected: rememberSelectedLead,
  } = useAppointmentLeadSearch({
    selectedLeadId: values.leadId,
    seedLeadId: leadSeedId,
  });

  const loadError =
    patientSeedError || leadSeedError ? t('toasts.loadDataFailedRetry') : null;

  useEffect(() => {
    if (patientSeedError || leadSeedError) {
      toast.error(t('toasts.loadDataFailed'));
    }
  }, [patientSeedError, leadSeedError, t]);

  const {
    baseline,
    baselineReady,
    adoptSuggestedVisitKind,
    handleTextDraftChange,
    handleBack,
    dialogOpen,
    allowNextNavigation,
    stay,
    leave,
  } = useAppointmentFormLifecycle({
    mode,
    initialValues,
    values,
    submitting,
    scopeClinicId,
    setScopeClinicId,
    setFieldValue: setValue,
    prefillPatientId,
    prefillLeadId,
    prefillClinicId,
    prefillDoctorId,
    prefillDate,
    prefillStartTime,
    patientSeedPending,
    leadSeedPending,
    patientSeedError,
    leadSeedError,
    selectedPatient,
    selectedLead,
    doctors,
    doctorsPending: isDoctorsListUnready,
    bookingAnchor,
    onClinicSynced: resetActiveChairCount,
    textFieldsRef,
    backTo,
    onBack,
  });

  const setVisitKind = useCallback(
    (next: AppointmentVisitKind) => {
      setValue('visitKind', next);
    },
    [setValue],
  );

  const { markVisitKindTouched } = useSuggestedVisitKind({
    mode,
    values,
    initialValues,
    baseline,
    baselineReady,
    excludeAppointmentId,
    setVisitKind,
    adoptSuggestedVisitKind,
  });

  const requestClose = useCallback(() => {
    if (presentation !== 'dialog') {
      handleBack();
      return;
    }
    void attemptFormDialogClose({
      busy: submitting,
      isDirty: true,
      confirm,
      onClose: () => {
        // Closing the overlay changes the URL. Skip the route blocker — the
        // unsaved dialog for that blocker is only mounted on the page form.
        allowNextNavigation();
        (onBack ?? handleBack)();
      },
    });
  }, [presentation, handleBack, submitting, confirm, onBack, allowNextNavigation]);

  const dismissDialog = useCallback(() => {
    if (submitting) return;
    allowNextNavigation();
    if (onBack) {
      onBack();
      return;
    }
    handleBack();
  }, [submitting, allowNextNavigation, onBack, handleBack]);

  const initialLoading =
    isBootstrapPending ||
    isDoctorsPending ||
    (Boolean(patientSeedId) && patientSeedPending) ||
    (Boolean(leadSeedId) && leadSeedPending);

  const coreLocked = mode === 'edit' && isTerminalStatus(initialValues.status);
  const fieldsDisabled = submitting || coreLocked;
  const endTimeLabel = calculateEndTimeLabel(values.startTime, values.duration);
  const startTimeValue = useMemo(() => {
    if (!values.startTime) return null;
    const parsed = parse(values.startTime, 'HH:mm', new Date());
    return isValid(parsed) ? parsed : null;
  }, [values.startTime]);

  const statusOptions = useMemo(() => {
    if (mode !== 'edit') {
      return APPOINTMENT_STATUS_OPTIONS.filter(
        (option) => option.value === 'scheduled' || option.value === 'confirmed'
      );
    }
    return getFormStatusOptions(initialValues.status);
  }, [mode, initialValues.status]);

  const applyCascade = useCallback(
    (field: AppointmentCascadeField, nextValue: unknown) => {
      const patch = applyAppointmentCascadeClear(values, field, nextValue);
      Object.entries(patch).forEach(([key, value]) => {
        setValue(key as FieldPath<AppointmentFormValues>, value as never);
      });
    },
    [values, setValue]
  );

  const handleDateChange = useCallback(
    (nextDate: Date | null) => {
      setValue('date', nextDate);
      if (nextDate) {
        clearErrors('date');
      }
      applyCascade('date', nextDate);
    },
    [setValue, clearErrors, applyCascade]
  );

  const handleStartTimeChange = useCallback(
    (time: string) => {
      setValue('startTime', time);
      if (time) {
        clearErrors('startTime');
      }
    },
    [setValue, clearErrors]
  );

  const handleTextFieldCommit = useCallback(
    (field: 'reasonForVisit' | 'notes', value: string) => {
      setValue(field, value, { shouldTouch: true });
    },
    [setValue]
  );

  const handleLeadInputChangeWrapped = useCallback(
    (event: unknown, value: string, reason: string) => {
      if (reason === 'input' || reason === 'clear') {
        leadInputRef.current = value;
      }
      handleLeadInputChange(event, value, reason);
    },
    [handleLeadInputChange]
  );

  const openQuickCreatePatient = useCallback(() => {
    setQuickCreateFirstName(patientInputRef.current);
    setQuickCreatePatientOpen(true);
  }, []);

  const openQuickCreateLead = useCallback(() => {
    setQuickCreateFirstName(leadInputRef.current);
    setQuickCreateLeadOpen(true);
  }, []);

  const subjectLocked = lockPatient || lockLead;
  const showPatientCreate = canCreatePatient && !fieldsDisabled && !subjectLocked;
  const showLeadCreate = canCreateLead && !fieldsDisabled && !subjectLocked;

  const resolvedTitle =
    lockPatient && selectedPatient
      ? t(mode === 'edit' ? 'pages.editTitleWithPatient' : 'pages.addTitleWithPatient', {
          name: displayPatientName(selectedPatient) || selectedPatient.name,
        })
      : lockLead && selectedLead
        ? t(mode === 'edit' ? 'pages.editTitleWithLead' : 'pages.addTitleWithLead', {
            name: selectedLead.name,
          })
        : title;

  const showTreatmentCreate = canCreateTreatment && !fieldsDisabled;
  const patientQuickCreate = useQuickCreateMenuPaper({
    canCreate: showPatientCreate,
    createLabel: t('fields.createNewPatient'),
    onQuickCreate: openQuickCreatePatient,
  });
  const leadQuickCreate = useQuickCreateMenuPaper({
    canCreate: showLeadCreate,
    createLabel: t('fields.createNewLead'),
    onQuickCreate: openQuickCreateLead,
  });
  const treatmentQuickCreate = useQuickCreateMenuPaper({
    canCreate: showTreatmentCreate,
    createLabel: t('fields.createNewTreatment'),
    onQuickCreate: () => setCreateTreatmentOpen(true),
  });

  const handlePatientCreated = useCallback(
    (created: Patient) => {
      rememberSelectedPatient(created);
      setValue('subjectType', 'patient');
      setValue('patientId', created._id);
      setValue('leadId', '');
      rememberSelectedLead(null);
      clearErrors('patientId');
      patientInputRef.current = '';
      toast.success(t('dialogs.quickCreateSuccessPatient'));
    },
    [rememberSelectedPatient, rememberSelectedLead, setValue, clearErrors, t]
  );

  const handleSelectExistingPatient = useCallback(
    (patient: Patient) => {
      rememberSelectedPatient(patient);
      setValue('subjectType', 'patient');
      setValue('patientId', patient._id);
      setValue('leadId', '');
      rememberSelectedLead(null);
      clearErrors('patientId');
      patientInputRef.current = '';
    },
    [rememberSelectedPatient, rememberSelectedLead, setValue, clearErrors]
  );

  const handleLeadCreated = useCallback(
    (created: Lead) => {
      rememberSelectedLead(created);
      setValue('subjectType', 'lead');
      setValue('leadId', created.id);
      setValue('patientId', '');
      rememberSelectedPatient(null);
      clearErrors('leadId');
      leadInputRef.current = '';
      toast.success(t('dialogs.quickCreateSuccessLead'));
    },
    [rememberSelectedLead, rememberSelectedPatient, setValue, clearErrors, t]
  );

  const handleSelectExistingLead = useCallback(
    (lead: Lead) => {
      rememberSelectedLead(lead);
      setValue('subjectType', 'lead');
      setValue('leadId', lead.id);
      setValue('patientId', '');
      rememberSelectedPatient(null);
      clearErrors('leadId');
      leadInputRef.current = '';
    },
    [rememberSelectedLead, rememberSelectedPatient, setValue, clearErrors]
  );

  const handleFormSubmit = useCallback(() => {
    setValue('reasonForVisit', textFieldsRef.current.reasonForVisit);
    setValue('notes', textFieldsRef.current.notes);
    void handleSubmit((data) => {
      const merged: AppointmentFormValues = {
        ...data,
        reasonForVisit: textFieldsRef.current.reasonForVisit,
        notes: textFieldsRef.current.notes,
      };
      const chairError = validateRequiredChair(
        merged,
        {
          requireChair: requireChairModeRef.current,
          activeChairCount: activeChairCountRef.current,
        },
        t
      );
      if (chairError) {
        setError('chairId', { type: 'manual', message: chairError });
        return;
      }
      onSubmit(merged);
    })();
  }, [
    setValue,
    handleSubmit,
    setError,
    requireChairModeRef,
    activeChairCountRef,
    t,
    onSubmit,
  ]);

  if (initialLoading) {
    return (
      <AppointmentFormShell
        presentation={presentation}
        title={resolvedTitle}
        onBack={requestClose}
        onDismiss={presentation === 'dialog' ? dismissDialog : undefined}
        onSubmit={() => undefined}
        submitLabel={submitLabel}
        busySubmitLabel={submittingLabel}
        open={appointmentDialogOpen}
        onExited={onDialogExited}
      >
        <AppointmentFormSkeleton />
      </AppointmentFormShell>
    );
  }

  return (
    <>
    <AppointmentFormShell
      presentation={presentation}
      title={resolvedTitle}
      onBack={requestClose}
      onDismiss={presentation === 'dialog' ? dismissDialog : undefined}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={presentation === 'page' && submitting ? submittingLabel : submitLabel}
      busySubmitLabel={submittingLabel}
      submitting={submitting}
      error={error || loadError}
      open={appointmentDialogOpen}
      onExited={onDialogExited}
    >
      <FormProvider {...form}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          // Bleed into FormPage Paper padding so the divider reaches card edges.
          ...(presentation === 'page' ? { mx: -3, my: -3 } : null),
        }}
      >
        <Box
          sx={{
            flex: { md: '1 1 42%' },
            maxWidth: { md: '42%' },
            width: '100%',
            p: 3,
          }}
        >
          <AppointmentSchedulePanel
            clinicId={values.clinicId}
            staffId={values.doctorId}
            date={values.date}
            bookingPolicy={bookingPolicy}
            onDateChange={handleDateChange}
            disabled={fieldsDisabled}
            dateError={fieldHelper('date').error}
            dateHelperText={fieldHelper('date').helperText}
            disablePast={mode === 'create'}
            preserveSelection={mode === 'edit'}
            excludeAppointmentId={excludeAppointmentId}
          />
        </Box>

        <Box
          sx={{
            flex: { md: '1 1 58%' },
            maxWidth: { md: '58%' },
            width: '100%',
            p: 3,
          }}
        >
            {coreLocked && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('status.coreLockedNotice', {
                  status: getStatusLabel(values.status).toLowerCase(),
                })}
              </Alert>
            )}

            {showPrefillWarnBanner && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('schedule.prefillWarnBanner')}
              </Alert>
            )}

            {selectedDoctor?.status === 'warn' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('schedule.conflictPreviewTitle')}
                </Typography>
                {selectedDoctor.message || t('schedule.conflictPreviewBody')}
              </Alert>
            )}

            {!subjectLocked && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              color="primary"
              value={values.subjectType}
              disabled={fieldsDisabled}
              onChange={(_event, next: AppointmentSubjectType | null) => {
                if (!next || next === values.subjectType) return;
                setQuickCreatePatientOpen(false);
                setQuickCreateLeadOpen(false);
                setValue('subjectType', next);
                if (next === 'patient') {
                  setValue('leadId', '');
                  rememberSelectedLead(null);
                } else {
                  setValue('patientId', '');
                  rememberSelectedPatient(null);
                }
              }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="patient">{t('fields.subjectPatient')}</ToggleButton>
              <ToggleButton value="lead">{t('fields.subjectLead')}</ToggleButton>
            </ToggleButtonGroup>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                {lockPatient || (!lockLead && values.subjectType === 'patient') ? (
                  lockPatient ? (
                    <TextField
                      id="patientId"
                      label={t('fields.patient')}
                      value={formatPatientInputLabel(selectedPatient)}
                      fullWidth
                      required
                      disabled
                      error={fieldHelper('patientId').error}
                      helperText={fieldHelper('patientId').helperText}
                    />
                  ) : (
                    <PatientSelect
                      id="patientId"
                      value={selectedPatient}
                      search={patientSearch}
                      onChange={(newValue) => {
                        setValue('patientId', newValue ? newValue._id : '');
                      }}
                      onInputValueChange={(value, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                          patientInputRef.current = value;
                        }
                      }}
                      slots={{ paper: patientQuickCreate.makeAutocompletePaper }}
                      label={t('fields.patient')}
                      placeholder={t('fields.patientSearchPlaceholder')}
                      error={fieldHelper('patientId').error}
                      helperText={fieldHelper('patientId').helperText}
                      disabled={fieldsDisabled}
                      required
                    />
                  )
                ) : lockLead ? (
                  <TextField
                    id="leadId"
                    label={t('fields.selectLead')}
                    value={
                      selectedLead
                        ? formatPatientOptionLabel(selectedLead.name, selectedLead.phone)
                        : ''
                    }
                    fullWidth
                    required
                    disabled
                    error={fieldHelper('leadId').error}
                    helperText={fieldHelper('leadId').helperText}
                  />
                ) : (
                  <Autocomplete
                    id="leadId"
                    forcePopupIcon={selectedLead ? false : 'auto'}
                    sx={
                      selectedLead
                        ? {
                            '&:not(:hover):not(.Mui-focused) .MuiAutocomplete-endAdornment': {
                              display: 'none',
                            },
                            '&:not(:hover):not(.Mui-focused) .MuiOutlinedInput-root': {
                              pr: '8px !important',
                            },
                          }
                        : undefined
                    }
                    options={leadOptions}
                    loading={leadSearching || leadSeedPending}
                    loadingText={t('common:loading')}
                    noOptionsText={
                      leadQueryTooShort
                        ? t('fields.searchTypeMore')
                        : t('fields.leadSearchNoResults')
                    }
                    filterOptions={(x) => x}
                    getOptionLabel={(option: Lead) =>
                      formatPatientOptionLabel(option.name, option.phone)
                    }
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={selectedLead}
                    onChange={(_event, newValue: Lead | null) => {
                      rememberSelectedLead(newValue);
                      setValue('leadId', newValue ? newValue.id : '');
                    }}
                    onInputChange={handleLeadInputChangeWrapped}
                    onOpen={handleLeadOpen}
                    slots={{ paper: leadQuickCreate.makeAutocompletePaper }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('fields.selectLead')}
                        placeholder={t('fields.leadSearchPlaceholder')}
                        error={fieldHelper('leadId').error}
                        helperText={fieldHelper('leadId').helperText}
                        disabled={fieldsDisabled}
                        required
                      />
                    )}
                    disabled={fieldsDisabled}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                  {coreLocked ? (
                    <TextField
                      label={t('fields.status')}
                      value={getStatusLabel(values.status)}
                      fullWidth
                      disabled
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <StatusSwatch status={values.status} />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel id="status-label">{t('fields.status')}</InputLabel>
                      <Select
                        labelId="status-label"
                        id="status"
                        name="status"
                        value={values.status}
                        onChange={(e) => setValue('status', String(e.target.value))}
                        label={t('fields.status')}
                        disabled={submitting}
                        renderValue={(selected) => (
                          <StatusOptionContent status={String(selected)} />
                        )}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            <StatusOptionContent status={status.value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
            </Grid>

            {values.status === 'cancelled' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={12}>
                  <TextField
                    id="cancelReason"
                    name="cancelReason"
                    label={t('fields.cancelReason')}
                    labelEnd={
                      <CharacterCount
                        current={values.cancelReason.length}
                        max={CANCEL_REASON_MAX_LENGTH}
                      />
                    }
                    value={values.cancelReason}
                    onChange={(e) => setValue('cancelReason', e.target.value)}
                    error={fieldHelper('cancelReason').error}
                    helperText={fieldHelper('cancelReason').helperText}
                    fullWidth
                    required={!coreLocked}
                    disabled={submitting || coreLocked}
                    slotProps={{
                      htmlInput: { maxLength: CANCEL_REASON_MAX_LENGTH },
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  required
                  error={fieldHelper('doctorId').error && !isDoctorsListUnready}
                >
                  <InputLabel id="doctor-label" required>
                    {t('fields.doctor')}
                  </InputLabel>
                  <Select
                    labelId="doctor-label"
                    id="doctorId"
                    name="doctorId"
                    value={values.doctorId}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <SelectPlaceholder>{t('fields.doctorPlaceholder')}</SelectPlaceholder>
                        );
                      }
                      const doctor = doctors.find((item) => item.id === selected);
                      if (!doctor) return String(selected);
                      return <DoctorOptionContent doctor={doctor} />;
                    }}
                    onChange={(e) => {
                      const doctorId = String(e.target.value);
                      setValue('doctorId', doctorId);
                      applyCascade('doctorId', doctorId);
                    }}
                    label={t('fields.doctor')}
                    disabled={fieldsDisabled || isDoctorsListUnready}
                  >
                    <MenuItem value="">{t('fields.doctorPlaceholder')}</MenuItem>
                    {doctors.map((doctor: BookingDoctorOption) => {
                      const blocked =
                        bookingAnchor === 'date' &&
                        (doctor.status === 'blocked' || doctor.status === 'not_working');
                      return (
                        <MenuItem
                          key={doctor.id}
                          value={doctor.id}
                          disabled={blocked}
                        >
                          <DoctorOptionContent doctor={doctor} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {isDoctorsListUnready ? (
                    <FormHelperText>
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                      >
                        <CircularProgress size={12} />
                        {t('fields.loadingDoctors')}
                      </Box>
                    </FormHelperText>
                  ) : fieldHelper('doctorId').helperText ? (
                    <FormHelperText>{fieldHelper('doctorId').helperText}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <ClinicScopeSelect
                  clinics={clinics}
                  value={values.clinicId}
                  onChange={(clinicId) => {
                    setScopeClinicId(clinicId);
                    setValue('clinicId', clinicId);
                    applyCascade('clinicId', clinicId);
                    resetActiveChairCount();
                  }}
                  showPicker={showClinicPicker}
                  scopedClinicName={
                    clinicDisplayName(clinics.find((clinic) => clinic._id === values.clinicId)) ||
                    scopedClinicName
                  }
                  disabled={fieldsDisabled}
                  label={t('fields.clinic')}
                  error={fieldHelper('clinicId').helperText ?? null}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <AppointmentTypeSelect
                  value={values.type}
                  onChange={(typeId, type) => {
                    setValue('type', typeId);
                    applyCascade('type', typeId);
                    if (
                      !values.treatmentId &&
                      typeof type?.defaultDuration === 'number'
                    ) {
                      setValue('duration', type.defaultDuration);
                      applyCascade('duration', type.defaultDuration);
                    }
                  }}
                  disabled={fieldsDisabled}
                  includeInactiveIds={includeInactiveTypeIds}
                  error={fieldHelper('type').helperText ?? null}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  required
                  disabled={fieldsDisabled}
                  error={fieldHelper('visitKind').error}
                  sx={{
                    '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
                    '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
                  }}
                >
                  <InputLabel shrink>{t('fields.visitKind')}</InputLabel>
                  <Select
                    value={values.visitKind}
                    displayEmpty
                    notched
                    label={t('fields.visitKind')}
                    renderValue={(selected) => {
                      if (selected === 'new') return t('visitKind.new');
                      if (selected === 'follow_up') return t('visitKind.followUp');
                      return (
                        <SelectPlaceholder>{t('fields.visitKindPlaceholder')}</SelectPlaceholder>
                      );
                    }}
                    onChange={(event) => {
                      markVisitKindTouched();
                      setValue('visitKind', String(event.target.value) as AppointmentVisitKind);
                    }}
                  >
                    <MenuItem value="new">{t('visitKind.new')}</MenuItem>
                    <MenuItem value="follow_up">{t('visitKind.followUp')}</MenuItem>
                  </Select>
                  {fieldHelper('visitKind').helperText ? (
                    <FormHelperText>{fieldHelper('visitKind').helperText}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TreatmentServiceAutocomplete
                  id="treatmentId"
                  fullWidth
                  options={treatmentOptions}
                  loading={catalogQuery.isPending}
                  disabled={fieldsDisabled}
                  value={selectedTreatment}
                  slots={{ paper: treatmentQuickCreate.makeAutocompletePaper }}
                  onChange={(_event, item) => {
                    setValue('treatmentId', item?._id ?? '');
                    if (item != null && Number.isFinite(item.duration) && item.duration > 0) {
                      setValue('duration', item.duration);
                      applyCascade('duration', item.duration);
                    }
                  }}
                  label={t('fields.treatment')}
                  placeholder={t('fields.treatmentPlaceholder')}
                  error={catalogQuery.isError}
                  helperText={
                    catalogQuery.isError ? t('toasts.loadDataFailedRetry') : undefined
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <AppointmentDurationField
                  value={values.duration}
                  onChange={(minutes) => {
                    setValue('duration', minutes);
                    applyCascade('duration', minutes);
                  }}
                  hasTreatment={Boolean(values.treatmentId)}
                  disabled={fieldsDisabled}
                  error={fieldHelper('duration').error}
                  helperText={fieldHelper('duration').helperText}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <ChairSelect
                  clinicId={values.clinicId}
                  value={values.chairId}
                  onChange={(chairId) => {
                    setValue('chairId', chairId);
                    applyCascade('chairId', chairId);
                  }}
                  disabled={fieldsDisabled}
                  required={chairRequired}
                  includeInactiveIds={includeInactiveChairIds}
                  bookingChairs={bookingChairs}
                  onActiveCountChange={handleActiveChairCountChange}
                  error={fieldHelper('chairId').helperText ?? null}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TimePicker
                  label={t('fields.startTime')}
                  required
                  disabled={fieldsDisabled}
                  value={startTimeValue}
                  onChange={(next) => {
                    if (next == null) {
                      handleStartTimeChange('');
                      return;
                    }
                    if (isValid(next)) {
                      handleStartTimeChange(format(next, 'HH:mm'));
                    }
                  }}
                  error={fieldHelper('startTime').error}
                  helperText={fieldHelper('startTime').helperText}
                  slotProps={{
                    textField: { placeholder: t('fields.startTimePlaceholder') },
                    field: { clearable: true },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('fields.endTime')}
                  value={endTimeLabel || ''}
                  placeholder={t('fields.endTimeAutoPlaceholder')}
                  disabled
                />
              </Grid>

              <AppointmentTextFields
                reasonForVisit={values.reasonForVisit}
                notes={values.notes}
                reasonError={fieldHelper('reasonForVisit').helperText}
                notesError={fieldHelper('notes').helperText}
                reasonTouched={isSubmitted}
                notesTouched={isSubmitted}
                disabled={fieldsDisabled}
                reasonRequired={false}
                notesDisabled={submitting}
                valuesRef={textFieldsRef}
                onDraftChange={handleTextDraftChange}
                onCommit={handleTextFieldCommit}
              />
            </Grid>
        </Box>
      </Box>
      </FormProvider>
    </AppointmentFormShell>

    {presentation === 'page' && (
    <Dialog
      open={dialogOpen}
      onClose={stay}
      aria-labelledby="unsaved-changes-title"
    >
      <DialogTitle id="unsaved-changes-title">
        {t('unsavedChanges.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('unsavedChanges.body')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={stay} intent="secondary">
          {t('unsavedChanges.stay')}
        </Button>
        <Button onClick={leave} intent="primary">
          {t('unsavedChanges.leave')}
        </Button>
      </DialogActions>
    </Dialog>
    )}

    {createTreatmentOpen ? (
      <TreatmentCatalogItemFormDialog
        title={tTreatment('catalog.addTitle')}
        mode="create"
        onClose={() => setCreateTreatmentOpen(false)}
        onSuccess={(created) => {
          setPinnedTreatment(created);
          setValue('treatmentId', created._id);
          if (Number.isFinite(created.duration) && created.duration > 0) {
            setValue('duration', created.duration);
            applyCascade('duration', created.duration);
          }
          setCreateTreatmentOpen(false);
        }}
      />
    ) : null}

    <QuickCreatePatientDialog
      open={quickCreatePatientOpen}
      initialFirstName={quickCreateFirstName}
      defaultClinicId={values.clinicId || scopeClinicId || ''}
      clinics={clinics}
      onClose={() => setQuickCreatePatientOpen(false)}
      onCreated={handlePatientCreated}
      onSelectExisting={handleSelectExistingPatient}
    />

    <QuickCreateLeadDialog
      open={quickCreateLeadOpen}
      initialFirstName={quickCreateFirstName}
      defaultClinicId={values.clinicId || scopeClinicId || ''}
      onClose={() => setQuickCreateLeadOpen(false)}
      onCreated={handleLeadCreated}
      onSelectExistingLead={handleSelectExistingLead}
      onSelectExistingPatient={handleSelectExistingPatient}
    />
    </>
  );
};

export default AppointmentForm;
